/*
 * SCSS → Tailwind token mapping (IntakeFormViewMode.scss deleted):
 *
 * .ifvm-container  -> pt-sp-4 px-sp-3 pb-sp-5 flex flex-col gap-sp-3 bg-deep-navy-5 min-h-full
 *   (padding-top: 32px = sp-4; padding-x: 24px = sp-3; padding-bottom: 40px = sp-5;
 *    gap: 24px = sp-3; background: var(--surface-secondary, #f6f7f8) = bg-deep-navy-5)
 *
 * Breadcrumb (ifvm-breadcrumb*): replaced by shared <Breadcrumb> component.
 *
 * .ifvm-title-row  -> flex items-center justify-between gap-sp-2
 * .ifvm-actions    -> flex items-center gap-sp-1
 * .ifvm-title      -> text-size-6 font-semibold leading-8 text-text-dark m-0
 *
 * No arbitrary values needed for this component.
 */
import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, EditIcon } from '@quickmd-llc/design-system'
import { INTAKE_FORM_PREFERRED_PHARMACY_QUESTION, MANAGEMENT_PAGE_ROUTE } from '../../../../../utils/constants'
import { tabValues, searchParamsKeys } from '../../../constants'
import IntakeFormViewModeTablet from '../IntakeFormViewModeTablet/IntakeFormViewModeTablet'
import { getFileFormats, getIntakeFormType, text } from './Constants'
import type { FileFormatOption } from './Constants'
import CreateEditIntakeView from '../../components/create-edit-intake-form-view/CreateEditIntakeView'
import { useIntakeForm } from '../../hooks/useIntakeForm'
import { useSaveIntakeForm } from '../../hooks/useSaveIntakeForm'
import type { IntakeFormRequestBody } from '../../hooks/useCreateIntakeForm'
import { isRecord, readStringProp, readOptionalProp } from '@/utils/narrowing'
import { Breadcrumb } from '@/components/breadcrumb/Breadcrumb'
import type { Crumb } from '@/components/breadcrumb/Breadcrumb'
import type { IntakeFormTemplateAdminApiOutContract } from '@/api/generated/pwrdbyQuickMDAdminsApi.schemas'

// ---------------------------------------------------------------------------
// Internal types for the builder object shape
// ---------------------------------------------------------------------------

type CheckboxesValues = {
  EnableMultipleAnswers: boolean
  isRequired: boolean
}

type BaseOption = {
  optionId: string
  optionValue: string
  optionLocalError: string
  isOptionError: boolean
}

type ConditionOption = BaseOption & {
  hasFollowUp: boolean
  followUpText: string
  followUpRequired: boolean
}

type QuestionOption = BaseOption | ConditionOption

type PreviouslySavedData = {
  questionTypeError: boolean
  questionTypeLocalError: string
  isSaved: boolean
  isPublished: boolean
  fileFormatOptions: FileFormatOption[]
  fileFormatError: boolean
  fileFormatLocalError: string
  message: string
  questionTypeValue: string
  checkboxesValues: CheckboxesValues
  options: QuestionOption[]
}

export type QuestionBuilderObject = {
  id: string
  IntakeFormQuestionId: string
  fileFormatOptions: FileFormatOption[]
  fileFormatError: boolean
  fileFormatLocalError: string
  isPreviouslySaved: boolean
  questionTypeValue: string
  rawType: string
  isRequired: boolean
  message: string
  previousSavedData: PreviouslySavedData
  checkboxesValues: CheckboxesValues
  options: QuestionOption[]
  questionTypeError: boolean
  questionTypeLocalError: string
  isSaved: boolean
  isPublished: boolean
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

type Props = {
  id: string
  error: string
  setError: (message: string) => void
}

// ---------------------------------------------------------------------------
// Raw-API question mapper — no `as`, no `any`
// ---------------------------------------------------------------------------

function mapRawQuestion(rawQuestion: unknown, i: number): QuestionBuilderObject {
  const t = isRecord(rawQuestion) ? rawQuestion : {}

  const questionType = readStringProp(t, 'Type')
  const questionTypeValue = getIntakeFormType(questionType)

  const rawQuestionText = readOptionalProp(t, 'Question')
  const questionText = typeof rawQuestionText === 'string' ? rawQuestionText : undefined
  const forcedMessage =
    questionType === 'bodymeasurement' && !questionText ? 'Height & Weight question'
    : questionType === 'pharmacyselection' ? INTAKE_FORM_PREFERRED_PHARMACY_QUESTION
    : (questionText ?? '')

  const intakeFormQuestionId = readStringProp(t, 'IntakeFormQuestionId')
  const rawFileTypes = readOptionalProp(t, 'FileTypes')
  const fileFormatOptions = getFileFormats(rawFileTypes)

  const rawIsRequired = readOptionalProp(t, 'IsRequired')
  const isRequired = rawIsRequired === true

  const rawIsMultiple = readOptionalProp(t, 'IsMultipleAnswerAllowed')
  const isMultipleAnswerAllowed = rawIsMultiple === true

  const checkboxesValues: CheckboxesValues = {
    EnableMultipleAnswers: isMultipleAnswerAllowed,
    isRequired,
  }

  const baseObj: QuestionBuilderObject = {
    id: `${i + 1}`,
    IntakeFormQuestionId: intakeFormQuestionId,
    fileFormatOptions,
    fileFormatError: false,
    fileFormatLocalError: '',
    isPreviouslySaved: true,
    questionTypeValue,
    rawType: questionType,
    isRequired,
    message: forcedMessage,
    previousSavedData: {
      questionTypeError: false,
      questionTypeLocalError: '',
      isSaved: true,
      isPublished: true,
      fileFormatOptions,
      fileFormatError: false,
      fileFormatLocalError: '',
      message: forcedMessage,
      questionTypeValue,
      checkboxesValues,
      options: [],
    },
    checkboxesValues,
    options: [],
    questionTypeError: false,
    questionTypeLocalError: '',
    isSaved: true,
    isPublished: true,
  }

  if (questionType === 'option') {
    const rawOptions = readOptionalProp(t, 'Options')
    const mappedOptions: BaseOption[] =
      Array.isArray(rawOptions) ?
        rawOptions.map((optionStr) => ({
          optionId: crypto.randomUUID(),
          optionValue: typeof optionStr === 'string' ? optionStr : '',
          optionLocalError: '',
          isOptionError: false,
        }))
      : []
    baseObj.options = mappedOptions
    baseObj.previousSavedData.options = mappedOptions.map((o) => ({ ...o }))
  } else if (questionType === 'condition') {
    const rawOptions = readOptionalProp(t, 'Options')
    const mappedOptions: ConditionOption[] =
      Array.isArray(rawOptions) ?
        rawOptions.map((opt) => {
          const optRecord = isRecord(opt) ? opt : {}
          const rawInput = readOptionalProp(optRecord, 'Input')
          const hasFollowUp = rawInput !== null && rawInput !== undefined
          const inputRecord = isRecord(rawInput) ? rawInput : {}
          return {
            optionId: crypto.randomUUID(),
            optionValue: readStringProp(optRecord, 'Name'),
            hasFollowUp,
            followUpText: hasFollowUp ? readStringProp(inputRecord, 'Name') : '',
            followUpRequired: hasFollowUp ? readOptionalProp(inputRecord, 'IsRequired') === true : false,
            optionLocalError: '',
            isOptionError: false,
          }
        })
      : []
    baseObj.options = mappedOptions
    baseObj.previousSavedData.options = mappedOptions.map((o) => ({ ...o }))
  } else if (questionType === 'bodymeasurement') {
    const heightOpt: BaseOption = {
      optionId: crypto.randomUUID(),
      optionValue: 'How tall are you?',
      optionLocalError: '',
      isOptionError: false,
    }
    const weightOpt: BaseOption = {
      optionId: crypto.randomUUID(),
      optionValue: 'What is your current weight? (lbs)',
      optionLocalError: '',
      isOptionError: false,
    }
    baseObj.options = [heightOpt, weightOpt]
    baseObj.previousSavedData.options = [{ ...heightOpt }, { ...weightOpt }]
  } else {
    baseObj.options = []
    baseObj.previousSavedData.options = []
  }

  return baseObj
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const IntakeFormViewMode = ({ id, error, setError }: Props) => {
  const navigate = useNavigate()
  const { intakeForm: intakeFormData } = useIntakeForm(id)
  const { saveIntakeForm } = useSaveIntakeForm(id)
  const [isEditMode, setIsEditMode] = useState<boolean>(false)

  /**
   * Resolves the raw Orval response to the expected contract shape.
   * The original JSX guarded against `.data` wrapping (a legacy Redux artifact —
   * the old getIntakeFormById reducer stored it as `{ data: ... }`). The Orval hook
   * never wraps, so this is a no-op in practice; preserved for safety via useMemo
   * so we avoid setState-in-effect.
   */
  const intakeFormDataForRender = useMemo((): IntakeFormTemplateAdminApiOutContract | undefined => {
    if (!intakeFormData) return intakeFormData
    if (isRecord(intakeFormData) && 'data' in intakeFormData) {
      const inner = readOptionalProp(intakeFormData, 'data')
      if (isRecord(inner)) {
        return {
          IntakeFormId: typeof inner['IntakeFormId'] === 'string' ? inner['IntakeFormId'] : null,
          Name: typeof inner['Name'] === 'string' ? inner['Name'] : null,
          Questions: Array.isArray(inner['Questions']) ? inner['Questions'] : null,
        }
      }
    }
    return intakeFormData
  }, [intakeFormData])

  /**
   * Maps raw API questions (Questions: unknown[] | null) to builder objects consumed
   * by CreateEditIntakeView. Each element is narrowed via isRecord + read helpers — no
   * `as` assertions on values.
   */
  const correctQuestionSectionArray: QuestionBuilderObject[] | null =
    intakeFormDataForRender && Array.isArray(intakeFormDataForRender.Questions) ?
      intakeFormDataForRender.Questions.map(mapRawQuestion)
    : null

  const onEditIntake = useCallback(() => setIsEditMode(true), [])
  const onCancelBtnPress = useCallback(() => setIsEditMode(false), [])

  const formName = intakeFormDataForRender?.Name ?? ''
  const intakeFormsUrl = `${MANAGEMENT_PAGE_ROUTE}?${searchParamsKeys.ACTIVE_TAB}=${tabValues.INTAKE_FORMS}`

  const crumbs: Crumb[] = [
    {
      label: 'Management',
      onClick: () => {
        void navigate(MANAGEMENT_PAGE_ROUTE)
      },
    },
    {
      label: 'Intake forms',
      onClick: () => {
        void navigate(intakeFormsUrl)
      },
    },
    { label: formName },
  ]

  if (isEditMode) {
    return (
      <CreateEditIntakeView
        isEdit
        title={formName}
        questions={correctQuestionSectionArray}
        error={error}
        saveIntakeForm={(body: IntakeFormRequestBody) => {
          setIsEditMode(false)
          saveIntakeForm(body, { onError: (message) => setError(message) })
        }}
        onCancelBtnPress={onCancelBtnPress}
        setIntakeFormError={(errorMsg: string) => setError(errorMsg)}
      />
    )
  }

  return (
    <div className='flex flex-1 flex-col gap-sp-3 bg-deep-navy-5 px-sp-3 pb-sp-5 pt-sp-4'>
      <Breadcrumb crumbs={crumbs} />

      <div className='flex items-center justify-between gap-sp-2'>
        <h1 className='m-0 text-size-6 font-semibold leading-8 text-text-dark'>{formName}</h1>
        <div className='flex items-center gap-sp-1'>
          <Button priority='primary' size='m' onClick={onEditIntake} data-testid='pw_if-edit-btn'>
            <EditIcon iconSize='m' />
            {text.EDIT_INTAKE}
          </Button>
        </div>
      </div>

      <IntakeFormViewModeTablet questionArray={correctQuestionSectionArray} />
    </div>
  )
}

export default IntakeFormViewMode
